import React, { useState } from 'react';
import { Link } from 'react-router-dom';
import { Button } from '../components/ui/button';
import { Card, CardContent } from '../components/ui/card';
import { Badge } from '../components/ui/badge';
import { mockData } from '../mock';
import { 
  Calendar, 
  Clock, 
  MapPin, 
  DollarSign, 
  Users, 
  ExternalLink, 
  ChevronRight,
  Briefcase,
  GraduationCap,
  Globe,
  CheckCircle,
  ArrowRight,
  Sparkles
} from 'lucide-react';

const Opportunities = () => {
  const [expandedOpportunity, setExpandedOpportunity] = useState(null);

  const opportunities = [
    {
      id: 1,
      type: 'Free Orientation Session',
      title: 'Remote Personal Injury Paralegal Roles in California (U.S.)',
      subtitle: 'Workflows, Compensation Structures, and Entry Pathways for Indian Law Students',
      status: 'Upcoming',
      date: '10th May 2026',
      time: '6:00 PM – 8:00 PM (IST)',
      mode: 'Online (Google Meet)',
      lastDateToRegister: '09th May 2026',
      registrationFee: 'Free of Cost',
      registrationLink: 'https://docs.google.com/forms/d/e/1FAIpQLSdk3jOczY0xElnHp4JJ9B2itI7b8dTuogHPXov0y4b__uavgg/viewform?usp=preview',
      compensation: {
        retainer: 'USD 1,500+/month',
        hourly: 'USD 25–40/hour'
      },
      highlights: [
        'No relocation required - Fully remote',
        'No U.S. degree or bar qualification needed',
        'Certificate from U.S. Bar-licensed attorneys',
        'Dedicated placement assistance'
      ],
      eligibility: [
        'Law students (all years)',
        'Law graduates',
        'Junior advocates',
        'Legal professionals interested in international legal workflows',
        'No prior U.S. law experience required'
      ],
      sessionCovers: [
        'Why Indian law graduates struggle financially in initial years',
        'How California Personal Injury litigation works behind the scenes',
        'The role of paralegals in settlement-focused PI practice',
        'Why U.S. firms are engaging remote paralegals',
        'Skills needed to become eligible for such roles'
      ],
      skills: [
        'Medical record review and summarisation',
        'Demand valuation and damage breakdowns',
        'Settlement demand preparation',
        'Case expense tracking and documentation'
      ],
      description: `Many law students in India face years of low or unpaid work before achieving financial stability. Meanwhile, U.S. Personal Injury firms operate on a settlement-driven, high-revenue model where paralegals play a critical role in case outcomes that can range from tens of thousands to several million dollars.

This free orientation session introduces Indian law students and legal professionals to remote paralegal career pathways with U.S. firms - a structured, global, and financially viable career track.`,
      note: 'Seats are limited to maintain clarity and interaction. Early registration is advised.'
    }
  ];

  return (
    <div className="min-h-screen bg-[#fafafa]">
      {/* Navigation */}
      <nav className="bg-white/80 backdrop-blur-md border-b border-slate-200 sticky top-0 z-50">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="flex justify-between items-center h-16">
            <Link to="/" className="flex items-center space-x-3">
              <img src={mockData.logo} alt="JurisGo Logo" className="h-9 w-9 object-contain" />
              <span className="text-xl font-bold text-slate-900">JurisGo</span>
            </Link>
            <div className="hidden md:flex items-center space-x-8">
              <Link to="/" className="text-slate-600 hover:text-slate-900 transition-colors text-sm font-medium">Home</Link>
              <Link to="/trainings" className="text-slate-600 hover:text-slate-900 transition-colors text-sm font-medium">Trainings</Link>
              <Link to="/opportunities" className="text-slate-900 font-semibold text-sm border-b-2 border-slate-900 pb-1">Opportunities</Link>
              <Link to="/corporate" className="text-slate-600 hover:text-slate-900 transition-colors text-sm font-medium">Corporate</Link>
              <Link to="/blog" className="text-slate-600 hover:text-slate-900 transition-colors text-sm font-medium">Blog</Link>
              <Link to="/wall-of-fame" className="text-slate-600 hover:text-slate-900 transition-colors text-sm font-medium">Wall of Fame</Link>
              <a 
                href="https://chat.whatsapp.com/JEsdqoYisesDUioAKKyHsx" 
                target="_blank" 
                rel="noopener noreferrer"
                className="bg-slate-900 text-white px-4 py-2 rounded-lg text-sm font-medium hover:bg-slate-800 transition-colors"
              >
                Join Community
              </a>
            </div>
          </div>
        </div>
      </nav>

      {/* Hero Section */}
      <section className="relative overflow-hidden bg-gradient-to-br from-slate-900 via-slate-800 to-slate-900 py-20">
        <div className="absolute inset-0 opacity-10">
          <div className="absolute top-10 left-10 w-72 h-72 bg-white rounded-full blur-3xl"></div>
          <div className="absolute bottom-10 right-10 w-96 h-96 bg-white rounded-full blur-3xl"></div>
        </div>
        <div className="relative max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 text-center">
          <Badge className="bg-white/10 text-white border-white/20 mb-6">
            <Sparkles className="h-3 w-3 mr-1" />
            Career Opportunities
          </Badge>
          <h1 className="text-4xl md:text-5xl font-bold text-white mb-6">
            Opportunities for
            <span className="block text-transparent bg-clip-text bg-gradient-to-r from-blue-400 to-cyan-400">
              Legal Professionals
            </span>
          </h1>
          <p className="text-lg text-white/70 max-w-2xl mx-auto">
            Discover remote paralegal roles, orientation sessions, and career pathways 
            with international law firms. Your gateway to global legal practice.
          </p>
        </div>
      </section>

      {/* Opportunities List */}
      <section className="py-16 px-4 sm:px-6 lg:px-8">
        <div className="max-w-5xl mx-auto">
          {opportunities.map((opp) => (
            <Card key={opp.id} className="overflow-hidden border-0 shadow-xl bg-white mb-8">
              {/* Header Banner */}
              <div className="bg-gradient-to-r from-slate-900 to-slate-800 p-6 text-white">
                <div className="flex flex-wrap items-center gap-3 mb-4">
                  <Badge className="bg-green-500/20 text-green-300 border-green-500/30">
                    {opp.status}
                  </Badge>
                  <Badge className="bg-blue-500/20 text-blue-300 border-blue-500/30">
                    {opp.type}
                  </Badge>
                  <Badge className="bg-amber-500/20 text-amber-300 border-amber-500/30">
                    {opp.registrationFee}
                  </Badge>
                </div>
                <h2 className="text-2xl md:text-3xl font-bold mb-2">{opp.title}</h2>
                <p className="text-white/70">{opp.subtitle}</p>
              </div>

              <CardContent className="p-6">
                {/* Key Details Grid */}
                <div className="grid md:grid-cols-4 gap-4 mb-8">
                  <div className="bg-slate-50 rounded-xl p-4 text-center">
                    <Calendar className="h-6 w-6 text-slate-700 mx-auto mb-2" />
                    <p className="text-xs text-slate-500 mb-1">Date</p>
                    <p className="font-semibold text-slate-900">{opp.date}</p>
                  </div>
                  <div className="bg-slate-50 rounded-xl p-4 text-center">
                    <Clock className="h-6 w-6 text-slate-700 mx-auto mb-2" />
                    <p className="text-xs text-slate-500 mb-1">Time</p>
                    <p className="font-semibold text-slate-900 text-sm">{opp.time}</p>
                  </div>
                  <div className="bg-slate-50 rounded-xl p-4 text-center">
                    <MapPin className="h-6 w-6 text-slate-700 mx-auto mb-2" />
                    <p className="text-xs text-slate-500 mb-1">Mode</p>
                    <p className="font-semibold text-slate-900">{opp.mode}</p>
                  </div>
                  <div className="bg-slate-50 rounded-xl p-4 text-center">
                    <DollarSign className="h-6 w-6 text-green-600 mx-auto mb-2" />
                    <p className="text-xs text-slate-500 mb-1">Potential Earnings</p>
                    <p className="font-semibold text-green-600">{opp.compensation.retainer}</p>
                  </div>
                </div>

                {/* Compensation Highlight */}
                <div className="bg-gradient-to-r from-green-50 to-emerald-50 border border-green-200 rounded-xl p-5 mb-8">
                  <h3 className="font-semibold text-green-800 mb-3 flex items-center">
                    <DollarSign className="h-5 w-5 mr-2" />
                    Compensation for Remote Paralegals
                  </h3>
                  <div className="grid md:grid-cols-2 gap-4">
                    <div className="bg-white/60 rounded-lg p-3">
                      <p className="text-sm text-slate-600">Monthly Retainer</p>
                      <p className="text-xl font-bold text-green-700">{opp.compensation.retainer}</p>
                    </div>
                    <div className="bg-white/60 rounded-lg p-3">
                      <p className="text-sm text-slate-600">Hourly Rate</p>
                      <p className="text-xl font-bold text-green-700">{opp.compensation.hourly}</p>
                    </div>
                  </div>
                </div>

                {/* Key Highlights */}
                <div className="mb-8">
                  <h3 className="font-semibold text-slate-900 mb-4 flex items-center">
                    <Sparkles className="h-5 w-5 mr-2 text-amber-500" />
                    Key Highlights
                  </h3>
                  <div className="grid md:grid-cols-2 gap-3">
                    {opp.highlights.map((highlight, idx) => (
                      <div key={idx} className="flex items-center gap-3 bg-amber-50 rounded-lg p-3">
                        <CheckCircle className="h-5 w-5 text-amber-600 flex-shrink-0" />
                        <span className="text-slate-700">{highlight}</span>
                      </div>
                    ))}
                  </div>
                </div>

                {/* Description */}
                <div className="mb-8">
                  <h3 className="font-semibold text-slate-900 mb-3 flex items-center">
                    <Briefcase className="h-5 w-5 mr-2 text-slate-700" />
                    About This Opportunity
                  </h3>
                  <p className="text-slate-600 leading-relaxed whitespace-pre-line">{opp.description}</p>
                </div>

                {/* What You'll Learn */}
                <div className="mb-8">
                  <h3 className="font-semibold text-slate-900 mb-4 flex items-center">
                    <GraduationCap className="h-5 w-5 mr-2 text-blue-600" />
                    What This Session Covers
                  </h3>
                  <div className="space-y-2">
                    {opp.sessionCovers.map((item, idx) => (
                      <div key={idx} className="flex items-start gap-3 p-3 bg-blue-50 rounded-lg">
                        <ChevronRight className="h-5 w-5 text-blue-600 flex-shrink-0 mt-0.5" />
                        <span className="text-slate-700">{item}</span>
                      </div>
                    ))}
                  </div>
                </div>

                {/* Skills You'll Gain */}
                <div className="mb-8">
                  <h3 className="font-semibold text-slate-900 mb-4 flex items-center">
                    <Globe className="h-5 w-5 mr-2 text-purple-600" />
                    Paralegal Skills in Demand
                  </h3>
                  <div className="grid md:grid-cols-2 gap-3">
                    {opp.skills.map((skill, idx) => (
                      <div key={idx} className="flex items-center gap-3 p-3 border border-purple-200 bg-purple-50 rounded-lg">
                        <div className="w-2 h-2 rounded-full bg-purple-500"></div>
                        <span className="text-slate-700">{skill}</span>
                      </div>
                    ))}
                  </div>
                </div>

                {/* Eligibility */}
                <div className="mb-8">
                  <h3 className="font-semibold text-slate-900 mb-4 flex items-center">
                    <Users className="h-5 w-5 mr-2 text-slate-700" />
                    Who Can Apply
                  </h3>
                  <div className="flex flex-wrap gap-2">
                    {opp.eligibility.map((item, idx) => (
                      <Badge key={idx} variant="outline" className="bg-slate-50 text-slate-700 border-slate-300 py-2 px-3">
                        {item}
                      </Badge>
                    ))}
                  </div>
                </div>

                {/* Important Note */}
                <div className="bg-red-50 border border-red-200 rounded-xl p-4 mb-8">
                  <p className="text-red-700 font-medium text-sm">
                    <span className="font-bold">Important:</span> {opp.note}
                  </p>
                  <p className="text-red-600 text-sm mt-2">
                    Last Date to Register: <span className="font-bold">{opp.lastDateToRegister}</span>
                  </p>
                </div>

                {/* CTA Buttons */}
                <div className="flex flex-col sm:flex-row gap-4">
                  <a 
                    href={opp.registrationLink}
                    target="_blank"
                    rel="noopener noreferrer"
                    className="flex-1"
                  >
                    <Button className="w-full h-14 bg-slate-900 hover:bg-slate-800 text-white text-lg font-semibold rounded-xl">
                      Register Now - It's Free
                      <ArrowRight className="ml-2 h-5 w-5" />
                    </Button>
                  </a>
                  <a 
                    href="https://chat.whatsapp.com/JEsdqoYisesDUioAKKyHsx"
                    target="_blank"
                    rel="noopener noreferrer"
                    className="flex-1"
                  >
                    <Button variant="outline" className="w-full h-14 border-2 border-slate-300 text-slate-700 text-lg font-semibold rounded-xl hover:bg-slate-50">
                      Join WhatsApp Group
                      <ExternalLink className="ml-2 h-5 w-5" />
                    </Button>
                  </a>
                </div>
              </CardContent>
            </Card>
          ))}
        </div>
      </section>

      {/* Footer */}
      <footer className="bg-slate-900 text-white py-12">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="grid md:grid-cols-4 gap-8">
            <div>
              <div className="flex items-center space-x-3 mb-4">
                <img src={mockData.logo} alt="JurisGo Logo" className="h-8 w-8 object-contain" />
                <span className="text-xl font-bold">JurisGo</span>
              </div>
              <p className="text-slate-400 text-sm">
                International legal training and professional development platform.
              </p>
            </div>
            <div>
              <h4 className="font-semibold mb-4">Quick Links</h4>
              <div className="space-y-2 text-sm text-slate-400">
                <Link to="/" className="block hover:text-white transition-colors">Home</Link>
                <Link to="/trainings" className="block hover:text-white transition-colors">Trainings</Link>
                <Link to="/opportunities" className="block hover:text-white transition-colors">Opportunities</Link>
                <Link to="/corporate" className="block hover:text-white transition-colors">Corporate</Link>
              </div>
            </div>
            <div>
              <h4 className="font-semibold mb-4">Contact</h4>
              <div className="space-y-2 text-sm text-slate-400">
                <p>admin@jurisgo.in</p>
                <p>+91 8130588175</p>
                <p>8th Floor, Vandana Building,<br/>Janpath, New Delhi - 110001</p>
              </div>
            </div>
            <div>
              <h4 className="font-semibold mb-4">Follow Us</h4>
              <div className="space-y-2 text-sm text-slate-400">
                <a href="https://instagram.com/juris_go" target="_blank" rel="noopener noreferrer" className="block hover:text-white transition-colors">
                  Instagram: @juris_go
                </a>
                <a href="https://jurisgo.in" target="_blank" rel="noopener noreferrer" className="block hover:text-white transition-colors">
                  Website: jurisgo.in
                </a>
              </div>
            </div>
          </div>
          <div className="border-t border-slate-800 mt-8 pt-8 text-center text-sm text-slate-500">
            <p>&copy; {new Date().getFullYear()} JurisGo. All rights reserved.</p>
          </div>
        </div>
      </footer>
    </div>
  );
};

export default Opportunities;
